import { useState } from 'react'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { useDiscoveryAssistantStore, type LocalMessage } from '@/stores/discoveryAssistantStore'

interface MultiStepQuestionMessageProps {
  message: LocalMessage
  accountId: string
  projectId: string
}

export function MultiStepQuestionMessage({ message, accountId, projectId }: MultiStepQuestionMessageProps) {
  const { multiStepData } = message
  if (!multiStepData) return null

  const submitMultiStepAnswer = useDiscoveryAssistantStore(state => state.submitMultiStepAnswer)

  const [currentStepIndex, setCurrentStepIndex] = useState(multiStepData.currentStepIndex || 0)
  const [stepAnswers, setStepAnswers] = useState<Record<string, string[]>>(
    multiStepData.steps.reduce((acc, step) => ({
      ...acc,
      [step.id]: step.selectedValues || []
    }), {})
  )
  const [isSubmitted, setIsSubmitted] = useState(false)

  const currentStep = multiStepData.steps[currentStepIndex]
  const isLastStep = currentStepIndex === multiStepData.steps.length - 1
  const isStepComplete = (stepId: string) => (stepAnswers[stepId]?.length || 0) > 0

  const allStepsComplete = multiStepData.steps.every(step => isStepComplete(step.id))

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStepIndex(currentStepIndex + 1)
    } else if (allStepsComplete) {
      handleSubmitAll()
    }
  }

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1)
    }
  }

  const handleSubmitAll = async () => {
    setIsSubmitted(true)
    await submitMultiStepAnswer(
      accountId,
      projectId,
      multiStepData.multiStepId,
      stepAnswers
    )
  }

  const handleStepSelection = (stepId: string, values: string[]) => {
    setStepAnswers({
      ...stepAnswers,
      [stepId]: values
    })
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] rounded-lg px-4 py-4 bg-warm-100 border border-warm-200">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-text">{multiStepData.title}</h3>
          <p className="text-xs text-text-muted">
            Step {currentStepIndex + 1} of {multiStepData.steps.length}
          </p>
        </div>

        <Tabs
          value={currentStep.id}
          onValueChange={(stepId) => {
            const idx = multiStepData.steps.findIndex(s => s.id === stepId)
            if (idx !== -1) setCurrentStepIndex(idx)
          }}
        >
          <TabsList className="w-full grid mb-4" style={{ gridTemplateColumns: `repeat(${multiStepData.steps.length}, 1fr)` }}>
            {multiStepData.steps.map((step) => (
              <TabsTrigger
                key={step.id}
                value={step.id}
                className="flex items-center justify-center gap-1.5 text-xs"
              >
                <span>{step.label}</span>
                {isStepComplete(step.id) && (
                  <Check className="w-3 h-3 text-market" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {multiStepData.steps.map((step) => (
            <TabsContent key={step.id} value={step.id} className="mt-0">
              <p className="text-sm font-medium text-warm-800 mb-3">{step.question}</p>

              {step.type === 'single' ? (
                <RadioGroup
                  value={stepAnswers[step.id]?.[0]}
                  onValueChange={(value) => handleStepSelection(step.id, [value])}
                  disabled={isSubmitted}
                >
                  <div className="space-y-2">
                    {step.options.map(option => (
                      <div
                        key={option.id}
                        className="flex items-start space-x-3 p-2 rounded-md hover:bg-warm-50 transition-colors"
                      >
                        <RadioGroupItem value={option.id} id={`${step.id}-${option.id}`} />
                        <label htmlFor={`${step.id}-${option.id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-warm-900">{option.label}</span>
                            {option.isRecommended && (
                              <Badge className="text-xs bg-market-light text-white">Recommended</Badge>
                            )}
                          </div>
                          {option.description && (
                            <p className="text-xs text-text-secondary mt-1">{option.description}</p>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              ) : (
                <div className="space-y-2">
                  {step.options.map(option => (
                    <div
                      key={option.id}
                      className="flex items-start space-x-3 p-2 rounded-md hover:bg-warm-50 transition-colors"
                    >
                      <Checkbox
                        id={`${step.id}-${option.id}`}
                        checked={stepAnswers[step.id]?.includes(option.id)}
                        onCheckedChange={(checked) => {
                          const current = stepAnswers[step.id] || []
                          if (checked) {
                            handleStepSelection(step.id, [...current, option.id])
                          } else {
                            handleStepSelection(step.id, current.filter(v => v !== option.id))
                          }
                        }}
                        disabled={isSubmitted}
                      />
                      <label htmlFor={`${step.id}-${option.id}`} className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-warm-900">{option.label}</span>
                          {option.isRecommended && (
                            <Badge className="text-xs bg-market-light text-white">Recommended</Badge>
                          )}
                        </div>
                        {option.description && (
                          <p className="text-xs text-text-secondary mt-1">{option.description}</p>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-4 flex justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStepIndex === 0 || isSubmitted}
            size="sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isStepComplete(currentStep.id) || isSubmitted}
            size="sm"
          >
            {isLastStep ? (
              isSubmitted ? 'Submitted' : 'Submit All Answers'
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
